import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { ChevronLeft, ChevronRight, Sparkles, Trash2, Lock, Unlock, CalendarCheck, AlertCircle, X, Loader2, RefreshCw, Repeat, ArrowRightLeft } from "lucide-react";
import toast from "react-hot-toast";

// ─── Types ────────────────────────────────────────────────────────────────────

interface SchedulePeriod {
  id: string;
  weekStart: string;
  version: number;
  status: "draft" | "published" | "superseded";
  publishedAt: string | null;
  notes: string | null;
}

interface ScheduleAssignment {
  id: string;
  schedulePeriodId: string;
  teamMemberId: string;
  customerId: string;
  assignmentDate: string;
  startsAt: string;
  endsAt: string;
  sourceTimezone: string;
  isPrimary: boolean;
  isLocked: boolean;
  replacesTeamMemberId: string | null;
  notes: string | null;
  aiEnhanced: boolean;
  aiReason: string | null;
  updatedAt: string;
}

interface Customer {
  id: string;
  name: string;
  reportTimezone: string | null;
  active: boolean;
}

interface TeamMember {
  id: string;
  firstName: string;
  lastName: string;
  team: string | null;
  region: string | null;
  assignedCount?: number;
}

interface LeaveEntry {
  teamMemberId: string;
  firstName: string;
  lastName: string;
  date: string; // YYYY-MM-DD
}

interface TimeOffEntry {
  id: string;
  teamMemberId: string;
  memberName: string;
  startDate: string;
  endDate: string;
  requestType: "time_off" | "sick" | "personal";
  reason: string | null;
  status: "approved" | "rejected";
  reviewNote: string | null;
  reviewedAt: string | null;
  createdAt: string;
}

interface RecurringTemplate {
  id: string;
  teamMemberId: string;
  customerId: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  sourceTimezone: string;
  startDate: string;
  endDate: string | null;
  isPrimary: boolean;
  notes: string | null;
}

const DAY_NAMES_FULL = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

interface CoverageGap {
  customerId: string;
  customerName: string;
  date: string;
  windowStart: string;
  windowEnd: string;
  reason: "no_trained_pccs" | "all_on_leave" | "no_shift_overlap";
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getAuthToken() {
  return localStorage.getItem("authToken") ?? "";
}

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${getAuthToken()}`, ...init.headers },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as any).message ?? `HTTP ${res.status}`);
  }
  return res.json();
}

function toMonday(dateStr: string): string {
  const d = new Date(dateStr + "T00:00:00Z");
  const dow = d.getUTCDay();
  const diff = dow === 0 ? -6 : 1 - dow;
  d.setUTCDate(d.getUTCDate() + diff);
  return d.toISOString().slice(0, 10);
}

function addDays(dateStr: string, n: number): string {
  const d = new Date(dateStr + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

function weekDays(monday: string): string[] {
  return Array.from({ length: 7 }, (_, i) => addDays(monday, i));
}

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function fmtDate(iso: string): string {
  const d = new Date(iso + "T00:00:00Z");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
}

function fmtWeekRange(monday: string): string {
  const sun = addDays(monday, 6);
  return `${fmtDate(monday)} – ${fmtDate(sun)}`;
}

function fmtTime(iso: string, tz: string): string {
  try {
    return new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true, timeZone: tz });
  } catch {
    return new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  }
}

function fmtTimeRange(startsAt: string, endsAt: string, tz: string): string {
  const start = new Date(startsAt);
  const end = new Date(endsAt);
  // 24h window = always_on; show "All Day" instead of "12:00 AM–12:00 AM"
  if (end.getTime() - start.getTime() >= 86_400_000 - 60_000) return "All Day";
  return `${fmtTime(startsAt, tz)}–${fmtTime(endsAt, tz)}`;
}

function localTimeToUtc(date: string, timeHHMM: string, tz: string): string {
  const [h, m] = timeHHMM.split(":").map(Number);
  const approx = new Date(`${date}T${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:00Z`);
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz, hour12: false, year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit",
  }).formatToParts(approx);
  const get = (type: string) => Number(parts.find(p => p.type === type)?.value ?? "0");
  const localH = get("hour") % 24;
  const localM = get("minute");
  const diffMs = ((h * 60 + m) - (localH * 60 + localM)) * 60_000;
  return new Date(approx.getTime() + diffMs).toISOString();
}

const GAP_REASON_LABEL: Record<CoverageGap["reason"], string> = {
  no_trained_pccs: "No trained PCCs",
  all_on_leave: "All trained PCCs on leave",
  no_shift_overlap: "No PCC shift overlaps this window",
};

// ─── Assignment Sidebar ────────────────────────────────────────────────────────

interface SidebarProps {
  periodId: string;
  customer: Customer;
  date: string;
  assignments: ScheduleAssignment[];
  allMembers: TeamMember[];
  onClose: () => void;
  onRefresh: () => void;
}

function AssignmentSidebar({ periodId, customer, date, assignments, allMembers, onClose, onRefresh }: SidebarProps) {
  const tz = customer.reportTimezone || "America/Los_Angeles";
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime]     = useState("17:00");
  const [memberId, setMemberId]   = useState("");
  const [isPrimary, setIsPrimary] = useState(true);
  const [notes, setNotes]         = useState("");
  const [eligible, setEligible]   = useState<TeamMember[]>([]);
  const [loadingElig, setLoadingElig] = useState(false);
  const [saving, setSaving]       = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [swappingRowId, setSwappingRowId] = useState<string | null>(null);
  const [swapMemberId, setSwapMemberId]   = useState("");
  const [swapReason, setSwapReason]       = useState("");
  const [swapSaving, setSwapSaving]       = useState(false);

  const fetchEligible = useCallback(async () => {
    if (!startTime || !endTime || startTime >= endTime) { setEligible([]); return; }
    setLoadingElig(true);
    try {
      const startsAt = localTimeToUtc(date, startTime, tz);
      const endsAt   = localTimeToUtc(date, endTime, tz);
      const params = new URLSearchParams({ customerId: customer.id, schedulePeriodId: periodId, startsAt, endsAt });
      const data = await api<{ eligible: TeamMember[] }>(`/api/v1/schedule/eligibility?${params}`);
      setEligible(data.eligible);
    } catch {
      setEligible(allMembers);
    } finally {
      setLoadingElig(false);
    }
  }, [date, startTime, endTime, tz, customer.id, periodId, allMembers]);

  useEffect(() => { void fetchEligible(); }, [fetchEligible]);

  const handleAdd = async () => {
    if (!memberId) { toast.error("Select a PCC"); return; }
    setSaving(true);
    try {
      const startsAt = localTimeToUtc(date, startTime, tz);
      const endsAt   = localTimeToUtc(date, endTime, tz);
      await api("/api/v1/schedule/assignments", {
        method: "POST",
        body: JSON.stringify({ schedulePeriodId: periodId, teamMemberId: memberId, customerId: customer.id, startsAt, endsAt, sourceTimezone: tz, isPrimary, notes: notes || null }),
      });
      setMemberId(""); setNotes("");
      toast.success("Assignment added");
      onRefresh();
    } catch (e: any) {
      toast.error(e.message ?? "Failed to add");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (a: ScheduleAssignment) => {
    setDeletingId(a.id);
    try {
      await api(`/api/v1/schedule/assignments/${a.id}`, { method: "DELETE" });
      toast.success("Removed");
      onRefresh();
    } catch (e: any) {
      toast.error(e.message ?? "Failed to remove");
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggleLock = async (a: ScheduleAssignment) => {
    setTogglingId(a.id);
    try {
      await api(`/api/v1/schedule/assignments/${a.id}`, {
        method: "PATCH",
        body: JSON.stringify({ updatedAt: a.updatedAt, isLocked: !a.isLocked }),
      });
      onRefresh();
    } catch (e: any) {
      toast.error(e.message ?? "Failed to update");
    } finally {
      setTogglingId(null);
    }
  };

  const handleSwap = async (a: ScheduleAssignment) => {
    if (!swapMemberId) { toast.error("Select a replacement PCC"); return; }
    setSwapSaving(true);
    try {
      await api(`/api/v1/schedule/assignments/${a.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          updatedAt: a.updatedAt,
          teamMemberId: swapMemberId,
          replacesTeamMemberId: a.teamMemberId,
          notes: swapReason.trim() || a.notes,
        }),
      });
      toast.success("Coverage swapped");
      setSwappingRowId(null); setSwapMemberId(""); setSwapReason("");
      onRefresh();
    } catch (e: any) {
      toast.error(e.message ?? "Failed to swap");
    } finally {
      setSwapSaving(false);
    }
  };

  const dayAssignments = assignments.filter(a => a.customerId === customer.id && a.assignmentDate === date);
  const memberMap = new Map(allMembers.map(m => [m.id, m]));

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white shadow-2xl flex flex-col h-full overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b bg-gray-50">
          <div>
            <p className="font-semibold text-gray-800">{customer.name}</p>
            <p className="text-xs text-gray-500">{fmtDate(date)} · {tz}</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-200 rounded-lg"><X className="h-4 w-4" /></button>
        </div>

        {/* Current assignments */}
        <div className="px-5 py-4 border-b">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Assigned PCCs ({dayAssignments.length})
          </p>
          {dayAssignments.length === 0 ? (
            <p className="text-sm text-gray-400 italic">No assignments for this day</p>
          ) : (
            <div className="space-y-2">
              {dayAssignments.map(a => {
                const m = memberMap.get(a.teamMemberId);
                const isSwapping = swappingRowId === a.id;
                return (
                  <div key={a.id} className="bg-gray-50 rounded-lg px-3 py-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-800">
                          {m ? `${m.firstName} ${m.lastName}` : a.teamMemberId.slice(0, 8)}
                          <span className={`ml-2 text-xs px-1.5 py-0.5 rounded-full ${a.isPrimary ? "bg-blue-100 text-blue-700" : a.replacesTeamMemberId ? "bg-orange-100 text-orange-700" : "bg-gray-200 text-gray-600"}`}>
                            {a.isPrimary ? "Primary" : a.replacesTeamMemberId ? "Replacement" : "Backup"}
                          </span>
                          {a.isLocked && <span className="ml-1 text-xs text-amber-600">🔒</span>}
                        </p>
                        <p className="text-xs text-gray-500">{fmtTimeRange(a.startsAt, a.endsAt, tz)}</p>
                      </div>
                      <div className="flex gap-1">
                        {!a.isLocked && (
                          <button
                            onClick={() => {
                              setSwappingRowId(isSwapping ? null : a.id);
                              setSwapMemberId(""); setSwapReason("");
                            }}
                            className={`p-1.5 rounded hover:bg-indigo-50 ${isSwapping ? "text-indigo-600 bg-indigo-50" : "text-gray-400 hover:text-indigo-500"}`}
                            title="Swap coverage — replace this PCC for this day only">
                            <ArrowRightLeft className="h-3.5 w-3.5" />
                          </button>
                        )}
                        <button onClick={() => handleToggleLock(a)} disabled={togglingId === a.id}
                          className="p-1.5 text-gray-400 hover:text-amber-500 hover:bg-amber-50 rounded"
                          title={a.isLocked ? "Unlock" : "Lock"}>
                          {a.isLocked ? <Unlock className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}
                        </button>
                        {!a.isLocked && (
                          <button onClick={() => handleDelete(a)} disabled={deletingId === a.id}
                            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </div>

                    {isSwapping && (
                      <div className="mt-2 pt-2 border-t border-gray-200 space-y-2">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Cover with</label>
                          <TypeaheadSelect
                            options={allMembers.filter(x => x.id !== a.teamMemberId).map(x => ({ id: x.id, label: `${x.firstName} ${x.lastName}` }))}
                            value={swapMemberId}
                            onChange={setSwapMemberId}
                            placeholder="Search replacement PCC…"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Reason (optional)</label>
                          <input value={swapReason} onChange={e => setSwapReason(e.target.value)}
                            placeholder="e.g. covering for PCC 1 — sick"
                            className="w-full border rounded-lg px-2 py-1.5 text-sm" />
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => handleSwap(a)} disabled={swapSaving || !swapMemberId}
                            className="flex-1 flex items-center justify-center gap-2 bg-indigo-600 text-white rounded-lg py-1.5 text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
                            {swapSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                            Confirm Swap
                          </button>
                          <button onClick={() => { setSwappingRowId(null); setSwapMemberId(""); setSwapReason(""); }}
                            className="px-3 py-1.5 text-sm text-gray-500 hover:bg-gray-100 rounded-lg">
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Add / override form */}
        <div className="px-5 py-4 flex-1">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Manual Override</p>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Start (local)</label>
                <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)}
                  className="w-full border rounded-lg px-2 py-1.5 text-sm" />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">End (local)</label>
                <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)}
                  className="w-full border rounded-lg px-2 py-1.5 text-sm" />
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">
                PCC {loadingElig && <span className="text-gray-400">(checking…)</span>}
              </label>
              <select value={memberId} onChange={e => setMemberId(e.target.value)}
                disabled={loadingElig}
                className="w-full border rounded-lg px-2 py-1.5 text-sm disabled:opacity-50 disabled:cursor-wait">
                <option value="">— select a PCC —</option>
                {(eligible.length > 0 ? eligible : allMembers).map(m => (
                  <option key={m.id} value={m.id}>
                    {m.firstName} {m.lastName}{m.assignedCount !== undefined ? ` (${m.assignedCount} assigned)` : ""}
                  </option>
                ))}
              </select>
              {eligible.length === 0 && !loadingElig && startTime && endTime && startTime < endTime && (
                <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" /> No eligible PCCs — showing all
                </p>
              )}
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={isPrimary} onChange={e => setIsPrimary(e.target.checked)} className="rounded" />
              Primary coverage
            </label>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Notes (optional)</label>
              <input value={notes} onChange={e => setNotes(e.target.value)}
                placeholder="e.g. covering for vacation"
                className="w-full border rounded-lg px-2 py-1.5 text-sm" />
            </div>
            <button onClick={handleAdd} disabled={saving || !memberId}
              className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white rounded-lg py-2 text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Add Assignment
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Bulk Swap Coverage modal ───────────────────────────────────────────────
// Same swap semantics as the single ⇄ swap in AssignmentSidebar (replace one PCC
// with another for one specific account/day, nothing else on either PCC's weekly
// schedule changes) — just applied to many accounts for one PCC/day at once, for
// PCCs who cover 10+ accounts and shouldn't need to repeat the swap per account.

interface BulkSwapModalProps {
  pccMember: TeamMember;
  weekAssignments: ScheduleAssignment[]; // this PCC's assignments across the visible week
  allMembers: TeamMember[];
  customers: Customer[];
  onClose: () => void;
  onRefresh: () => void;
}

function BulkSwapModal({ pccMember, weekAssignments, allMembers, customers, onClose, onRefresh }: BulkSwapModalProps) {
  const customerMap = useMemo(() => new Map(customers.map(c => [c.id, c])), [customers]);

  const datesWithAssignments = useMemo(
    () => Array.from(new Set(weekAssignments.map(a => a.assignmentDate))).sort(),
    [weekAssignments]
  );

  const [date, setDate] = useState(datesWithAssignments[0] ?? "");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [replacementId, setReplacementId] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const dayAssignments = useMemo(
    () => weekAssignments.filter(a => a.assignmentDate === date).sort((a, b) => {
      const nameA = customerMap.get(a.customerId)?.name ?? "";
      const nameB = customerMap.get(b.customerId)?.name ?? "";
      return nameA.localeCompare(nameB);
    }),
    [weekAssignments, date, customerMap]
  );

  // Reset selection to "all unlocked assignments for this date" whenever the date changes
  useEffect(() => {
    setSelectedIds(new Set(dayAssignments.filter(a => !a.isLocked).map(a => a.id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  const toggle = (id: string) =>
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const eligibleCount = dayAssignments.filter(a => !a.isLocked).length;
  const allEligibleSelected = eligibleCount > 0 && dayAssignments.every(a => a.isLocked || selectedIds.has(a.id));

  const toggleAll = () => {
    if (allEligibleSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(dayAssignments.filter(a => !a.isLocked).map(a => a.id)));
    }
  };

  const handleConfirm = async () => {
    if (!replacementId) { toast.error("Select a replacement PCC"); return; }
    if (selectedIds.size === 0) { toast.error("Select at least one account"); return; }
    setSaving(true);
    try {
      const data = await api<{ updated: number; skipped: { id: string; reason: string }[] }>(
        "/api/v1/schedule/assignments/bulk-swap",
        {
          method: "POST",
          body: JSON.stringify({
            assignmentIds: Array.from(selectedIds),
            teamMemberId: replacementId,
            notes: reason.trim() || undefined,
          }),
        }
      );
      if (data.updated > 0) {
        toast.success(`Swapped coverage on ${data.updated} account${data.updated !== 1 ? "s" : ""}`);
      }
      if (data.skipped.length > 0) {
        toast.error(`${data.skipped.length} account${data.skipped.length !== 1 ? "s" : ""} could not be swapped`);
      }
      onRefresh();
      if (data.updated > 0) onClose();
    } catch (e: any) {
      toast.error(e.message ?? "Bulk swap failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b sticky top-0 bg-white">
          <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <ArrowRightLeft className="h-5 w-5 text-indigo-500" /> Bulk Swap Coverage
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-sm text-gray-600">
            Replace <span className="font-semibold">{pccMember.firstName} {pccMember.lastName}</span> across multiple
            accounts for one day, in a single action. Nothing else on either PCC's weekly schedule changes.
          </p>

          {datesWithAssignments.length === 0 ? (
            <p className="text-sm text-gray-400 italic">No assignments this week for this PCC.</p>
          ) : (
            <>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Date</label>
                <select value={date} onChange={e => setDate(e.target.value)}
                  className="w-full border rounded-lg px-2 py-1.5 text-sm">
                  {datesWithAssignments.map(d => (
                    <option key={d} value={d}>{fmtDate(d)}</option>
                  ))}
                </select>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs text-gray-600">
                    Accounts ({selectedIds.size} of {eligibleCount} selected)
                  </label>
                  {eligibleCount > 0 && (
                    <button onClick={toggleAll} className="text-xs text-indigo-600 hover:underline">
                      {allEligibleSelected ? "Deselect all" : "Select all"}
                    </button>
                  )}
                </div>
                <div className="border rounded-lg divide-y max-h-56 overflow-y-auto">
                  {dayAssignments.map(a => {
                    const c = customerMap.get(a.customerId);
                    return (
                      <label key={a.id}
                        className={`flex items-center gap-2 px-3 py-2 text-sm ${a.isLocked ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:bg-gray-50"}`}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(a.id)}
                          disabled={a.isLocked}
                          onChange={() => toggle(a.id)}
                          className="rounded"
                        />
                        <span className="flex-1 min-w-0 truncate">{c?.name ?? a.customerId.slice(0, 8)}</span>
                        {a.isLocked && <span className="text-xs text-amber-600 shrink-0">🔒 locked</span>}
                      </label>
                    );
                  })}
                </div>
                {eligibleCount === 0 && (
                  <p className="text-xs text-amber-600 mt-1">
                    All assignments on this date are locked — unlock them first to include them in the swap.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs text-gray-600 mb-1">Cover with</label>
                <TypeaheadSelect
                  options={allMembers.filter(m => m.id !== pccMember.id).map(m => ({ id: m.id, label: `${m.firstName} ${m.lastName}` }))}
                  value={replacementId}
                  onChange={setReplacementId}
                  placeholder="Search replacement PCC…"
                />
              </div>

              <div>
                <label className="block text-xs text-gray-600 mb-1">Reason (optional)</label>
                <input value={reason} onChange={e => setReason(e.target.value)}
                  placeholder="e.g. Covering for PCC 1 — sick"
                  className="w-full border rounded-lg px-2 py-1.5 text-sm" />
              </div>

              <button onClick={handleConfirm} disabled={saving || !replacementId || selectedIds.size === 0}
                className="w-full flex items-center justify-center gap-2 bg-indigo-600 text-white rounded-lg py-2 text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRightLeft className="h-4 w-4" />}
                {saving ? "Swapping…" : `Confirm Swap (${selectedIds.size} account${selectedIds.size !== 1 ? "s" : ""})`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Gap Report Panel ─────────────────────────────────────────────────────────

function GapReport({ gaps, onDismiss }: { gaps: CoverageGap[]; onDismiss: () => void }) {
  if (!gaps.length) return null;
  return (
    <div className="rounded-xl border border-red-200 bg-red-50 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <AlertCircle className="h-4 w-4 text-red-500" />
          <span className="text-sm font-semibold text-red-700">{gaps.length} coverage gap{gaps.length !== 1 ? "s" : ""} — no PCC could be assigned</span>
        </div>
        <button onClick={onDismiss} className="text-red-400 hover:text-red-600">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="space-y-1 max-h-48 overflow-y-auto">
        {gaps.map((g, i) => (
          <div key={i} className="flex items-start gap-2 text-xs text-red-700 bg-white rounded px-3 py-1.5 border border-red-100">
            <span className="font-medium min-w-0 truncate flex-1">{g.customerName}</span>
            <span className="text-red-500 whitespace-nowrap">{fmtDate(g.date)} · {g.windowStart}–{g.windowEnd}</span>
            <span className="text-red-400 whitespace-nowrap">{GAP_REASON_LABEL[g.reason]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Typeable dropdown (searchable select, no external dependency) ────────────

function TypeaheadSelect({
  options, value, onChange, placeholder,
}: { options: { id: string; label: string }[]; value: string; onChange: (id: string) => void; placeholder: string }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  const selected = options.find(o => o.id === value);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const filtered = query.trim()
    ? options.filter(o => o.label.toLowerCase().includes(query.trim().toLowerCase()))
    : options;

  return (
    <div className="relative" ref={boxRef}>
      <input
        type="text"
        value={open ? query : (selected?.label ?? "")}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => { setQuery(""); setOpen(true); }}
        placeholder={placeholder}
        className="w-full border rounded-lg px-2 py-1.5 text-sm"
      />
      {open && (
        <div className="absolute z-20 mt-1 w-full max-h-48 overflow-y-auto bg-white border rounded-lg shadow-lg">
          {filtered.length === 0 ? (
            <p className="px-3 py-2 text-sm text-gray-400">No matches</p>
          ) : (
            filtered.map(o => (
              <button
                key={o.id}
                type="button"
                onClick={() => { onChange(o.id); setQuery(""); setOpen(false); }}
                className={`w-full text-left px-3 py-1.5 text-sm hover:bg-indigo-50 ${o.id === value ? "bg-indigo-50 text-indigo-700 font-medium" : "text-gray-700"}`}
              >
                {o.label}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ─── Recurring Assignments modal ───────────────────────────────────────────────

function RecurringTemplatesModal({
  customers, members, onClose,
}: { customers: Customer[]; members: TeamMember[]; onClose: () => void }) {
  const [templates, setTemplates] = useState<RecurringTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    teamMemberId: "", customerId: "", dayOfWeek: 6, startTime: "09:00", endTime: "13:00",
    sourceTimezone: "America/Los_Angeles", startDate: new Date().toISOString().slice(0, 10), endDate: "", notes: "",
  });

  const load = useCallback(() => {
    setLoading(true);
    api<{ templates: RecurringTemplate[] }>("/api/v1/schedule/recurring-templates")
      .then(d => setTemplates(d.templates))
      .catch(() => toast.error("Failed to load recurring assignments"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.teamMemberId || !form.customerId) { toast.error("Select a PCC and account"); return; }
    if (form.startTime >= form.endTime) { toast.error("Start time must be before end time"); return; }
    setSaving(true);
    try {
      await api("/api/v1/schedule/recurring-templates", {
        method: "POST",
        body: JSON.stringify({ ...form, endDate: form.endDate || null }),
      });
      toast.success("Recurring assignment created");
      setForm(f => ({ ...f, teamMemberId: "", customerId: "", notes: "" }));
      load();
    } catch (e: any) {
      toast.error(e.message ?? "Failed to create recurring assignment");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Remove this recurring assignment? Future weeks will stop generating it automatically. Already-generated weeks are unaffected.")) return;
    try {
      await api(`/api/v1/schedule/recurring-templates/${id}`, { method: "DELETE" });
      toast.success("Recurring assignment removed");
      load();
    } catch (e: any) {
      toast.error(e.message ?? "Failed to remove");
    }
  };

  const memberName = (id: string) => { const m = members.find(x => x.id === id); return m ? `${m.firstName} ${m.lastName}` : "Unknown"; };
  const customerName = (id: string) => customers.find(c => c.id === id)?.name ?? "Unknown";

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b sticky top-0 bg-white">
          <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <Repeat className="h-5 w-5 text-indigo-500" /> Recurring Assignments
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>

        <div className="p-5 space-y-5">
          <div className="bg-gray-50 rounded-xl p-4 space-y-3">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">New Recurring Assignment</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">PCC</label>
                <TypeaheadSelect
                  options={members.map(m => ({ id: m.id, label: `${m.firstName} ${m.lastName}` }))}
                  value={form.teamMemberId}
                  onChange={id => setForm(f => ({ ...f, teamMemberId: id }))}
                  placeholder="Type to search PCCs…"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Account</label>
                <TypeaheadSelect
                  options={customers.map(c => ({ id: c.id, label: c.name }))}
                  value={form.customerId}
                  onChange={id => {
                    const c = customers.find(cc => cc.id === id);
                    setForm(f => ({ ...f, customerId: id, sourceTimezone: c?.reportTimezone || f.sourceTimezone }));
                  }}
                  placeholder="Type to search accounts…"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Day of week</label>
                <select value={form.dayOfWeek} onChange={e => setForm(f => ({ ...f, dayOfWeek: Number(e.target.value) }))}
                  className="w-full border rounded-lg px-2 py-1.5 text-sm">
                  {DAY_NAMES_FULL.map((d, i) => <option key={i} value={i}>{d}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Start</label>
                  <input type="time" value={form.startTime} onChange={e => setForm(f => ({ ...f, startTime: e.target.value }))}
                    className="w-full border rounded-lg px-2 py-1.5 text-sm" />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">End</label>
                  <input type="time" value={form.endTime} onChange={e => setForm(f => ({ ...f, endTime: e.target.value }))}
                    className="w-full border rounded-lg px-2 py-1.5 text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Starts (week of)</label>
                <input type="date" value={form.startDate} onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))}
                  className="w-full border rounded-lg px-2 py-1.5 text-sm" />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Repeat until (optional)</label>
                <input type="date" value={form.endDate} onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))}
                  className="w-full border rounded-lg px-2 py-1.5 text-sm" />
              </div>
            </div>
            <p className="text-xs text-gray-400">
              If "Repeat until" is left blank, this assignment repeats every week indefinitely — it will keep
              generating automatically until you come back and set an end date or delete it.
            </p>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Notes (optional)</label>
              <input type="text" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                className="w-full border rounded-lg px-2 py-1.5 text-sm" placeholder="e.g. Saturday backup coverage" />
            </div>
            <button onClick={handleCreate} disabled={saving}
              className="w-full bg-indigo-600 text-white rounded-lg py-2 text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-1.5">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Repeat className="h-4 w-4" />}
              {saving ? "Saving…" : "Create Recurring Assignment"}
            </button>
          </div>

          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Active Recurring Assignments</p>
            {loading ? (
              <p className="text-sm text-gray-400 text-center py-6">Loading…</p>
            ) : templates.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-6">No recurring assignments yet</p>
            ) : (
              <div className="space-y-2">
                {templates.map(t => (
                  <div key={t.id} className="flex items-center justify-between border rounded-lg p-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{memberName(t.teamMemberId)} → {customerName(t.customerId)}</p>
                      <p className="text-xs text-gray-500">
                        Every {DAY_NAMES_FULL[t.dayOfWeek]}, {t.startTime}–{t.endTime} ({t.sourceTimezone.replace("America/", "")})
                        {" · "}From {t.startDate}{t.endDate ? ` to ${t.endDate}` : " (no end date)"}
                      </p>
                      {t.notes && <p className="text-xs text-gray-400 mt-0.5 truncate">{t.notes}</p>}
                    </div>
                    <button onClick={() => handleDelete(t.id)} className="text-gray-400 hover:text-red-500 p-1.5 shrink-0" title="Remove">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function PCCSchedule() {
  const today = new Date().toISOString().slice(0, 10);
  const [weekStart, setWeekStart] = useState(() => toMonday(today));
  const [period, setPeriod]       = useState<SchedulePeriod | null>(null);
  const [assignments, setAssignments] = useState<ScheduleAssignment[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [members, setMembers]     = useState<TeamMember[]>([]);
  const [loading, setLoading]     = useState(true);
  const [generating, setGenerating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [revisioning, setRevisioning] = useState(false);
  const [enhancing, setEnhancing] = useState(false);
  const [gaps, setGaps]           = useState<CoverageGap[]>([]);
  const [showGaps, setShowGaps]   = useState(false);
  const [leave, setLeave]         = useState<LeaveEntry[]>([]);
  const [sidebar, setSidebar]     = useState<{ customer: Customer; date: string } | null>(null);
  const [showRecurring, setShowRecurring] = useState(false);
  const [showBulkSwap, setShowBulkSwap] = useState(false);
  const [pccFilterId, setPccFilterId] = useState("");
  const [view, setView] = useState<"schedule" | "time-off">("schedule");
  const [timeOffList, setTimeOffList] = useState<TimeOffEntry[]>([]);
  const [timeOffLoading, setTimeOffLoading] = useState(false);
  const [timeOffStatusFilter, setTimeOffStatusFilter] = useState<"" | "approved" | "rejected">("");

  const days = weekDays(weekStart);

  useEffect(() => {
    const token = getAuthToken();
    Promise.all([
      fetch("/api/v1/customer-profiles?isActive=true&limit=500", { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json()).then(d => ((d.data ?? d.customerProfiles ?? []) as any[]).map(c => ({ ...c, name: c.qboDisplayName ?? c.name ?? "" }))),
      fetch("/api/v1/team-members?isActive=true&limit=500", { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json()).then(d => (d.data ?? d.teamMembers ?? []) as TeamMember[]),
    ]).then(([c, m]) => {
      setCustomers(c.filter((x: any) => x.isActive !== false));
      setMembers(m);
    }).catch(() => {});
  }, []);

  const loadPeriod = useCallback(async () => {
    setLoading(true);
    try {
      const [data, leaveData] = await Promise.all([
        api<{ periods: SchedulePeriod[] }>(`/api/v1/schedule/periods?weekStart=${weekStart}`),
        api<{ leave: LeaveEntry[] }>(`/api/v1/schedule/leave?weekStart=${weekStart}`).catch(() => ({ leave: [] })),
      ]);
      setLeave(leaveData.leave);
      const priority: Record<string, number> = { draft: 0, published: 1, superseded: 2 };
      const sorted = [...data.periods].sort((a, b) => priority[a.status] - priority[b.status]);
      const p = sorted[0] ?? null;
      setPeriod(p);
      if (p) {
        const detail = await api<{ assignments: ScheduleAssignment[] }>(`/api/v1/schedule/periods/${p.id}`);
        setAssignments(detail.assignments);
      } else {
        setAssignments([]);
      }
    } catch {
      setPeriod(null); setAssignments([]);
    } finally {
      setLoading(false);
    }
  }, [weekStart]);

  const loadTimeOff = useCallback(async () => {
    setTimeOffLoading(true);
    try {
      const params = timeOffStatusFilter ? `?status=${timeOffStatusFilter}` : "";
      const data = await api<{ requests: TimeOffEntry[] }>(`/api/v1/schedule/time-off${params}`);
      setTimeOffList(data.requests);
    } catch {
      setTimeOffList([]);
      toast.error("Failed to load time off list");
    } finally {
      setTimeOffLoading(false);
    }
  }, [timeOffStatusFilter]);

  useEffect(() => {
    if (view === "time-off") void loadTimeOff();
  }, [view, loadTimeOff]);

  useEffect(() => {
    setGaps([]); setShowGaps(false);
    void loadPeriod();
  }, [loadPeriod]);

  const handleGenerate = async (regenerate = false) => {
    if (regenerate && period?.status === "draft") {
      const ok = window.confirm(
        "This will clear all unlocked assignments and regenerate the schedule. Locked assignments are preserved. Continue?"
      );
      if (!ok) return;
    }
    setGenerating(true);
    try {
      const data = await api<{ period: SchedulePeriod; assigned: number; gaps: CoverageGap[] }>(
        "/api/v1/schedule/generate",
        { method: "POST", body: JSON.stringify({ weekStart }) }
      );
      setPeriod(data.period);
      setGaps(data.gaps);
      setShowGaps(data.gaps.length > 0);
      // Reload full assignments
      const detail = await api<{ assignments: ScheduleAssignment[] }>(`/api/v1/schedule/periods/${data.period.id}`);
      setAssignments(detail.assignments);
      if (data.gaps.length > 0) {
        toast.success(`Generated ${data.assigned} assignments · ${data.gaps.length} gaps need attention`);
      } else {
        toast.success(`Generated ${data.assigned} assignments — full coverage`);
      }
    } catch (e: any) {
      toast.error(e.message ?? "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const handlePublish = async () => {
    if (!period) return;
    setPublishing(true);
    try {
      await api(`/api/v1/schedule/periods/${period.id}/publish`, { method: "PATCH", body: JSON.stringify({}) });
      toast.success("Schedule published — PCCs can see it in their portal");
      await loadPeriod();
    } catch (e: any) {
      toast.error(e.message ?? "Failed to publish");
    } finally {
      setPublishing(false);
    }
  };

  const handleEnhance = async () => {
    if (!period) return;
    setEnhancing(true);
    try {
      const data = await api<{ enhanced: number; unchanged: number; warnings: string[] }>(
        "/api/v1/schedule/enhance-with-ai",
        { method: "POST", body: JSON.stringify({ periodId: period.id }) }
      );
      if (data.enhanced === 0) {
        toast("No changes — schedule is already optimal", { icon: "✅" });
      } else {
        toast.success(`Optimized ${data.enhanced} assignment${data.enhanced !== 1 ? "s" : ""}`);
      }
      await loadPeriod();
    } catch (e: any) {
      toast.error(e.message ?? "Optimization failed");
    } finally {
      setEnhancing(false);
    }
  };

  const handleRevise = async () => {
    setRevisioning(true);
    try {
      const data = await api<{ period: SchedulePeriod; clonedAssignments: number }>(
        "/api/v1/schedule/periods/republish",
        { method: "POST", body: JSON.stringify({ weekStart }) }
      );
      toast.success(`Revision draft created with ${data.clonedAssignments} cloned assignments`);
      await loadPeriod();
    } catch (e: any) {
      toast.error(e.message ?? "Failed to create revision");
    } finally {
      setRevisioning(false);
    }
  };

  // Lookup maps — all memoized so they only rebuild when their data changes
  const assignmentMap = useMemo(() => {
    const map = new Map<string, ScheduleAssignment[]>();
    for (const a of assignments) {
      const key = `${a.customerId}|${a.assignmentDate}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(a);
    }
    return map;
  }, [assignments]);

  const memberMap = useMemo(() => new Map(members.map(m => [m.id, m])), [members]);

  const leaveSet = useMemo(() => new Set(leave.map(l => `${l.teamMemberId}|${l.date}`)), [leave]);

  const isDraft     = period?.status === "draft";
  const isPublished = period?.status === "published";

  // When a period exists, only show customers the rule engine actually considered
  // (those with assignments or coverage gaps). Without a period, show all active customers.
  const displayCustomers = useMemo(() => {
    let result = customers;
    if (period) {
      const inSchedule = new Set<string>();
      assignments.forEach(a => inSchedule.add(a.customerId));
      gaps.forEach(g => inSchedule.add(g.customerId));
      if (inSchedule.size > 0) result = customers.filter(c => inSchedule.has(c.id));
    }
    // Searching for a specific PCC narrows the grid to just the accounts they're
    // assigned to this week, so their shifts are easy to find at a glance. Also keep
    // accounts where this PCC was JUST swapped out (replacesTeamMemberId points back
    // to them) so a swap doesn't make the account silently vanish from the filtered
    // view — it stays visible showing who's now covering it.
    if (pccFilterId) {
      const customerIdsForMember = new Set(
        assignments
          .filter(a => a.teamMemberId === pccFilterId || a.replacesTeamMemberId === pccFilterId)
          .map(a => a.customerId)
      );
      result = result.filter(c => customerIdsForMember.has(c.id));
    }
    return result;
  }, [period, assignments, gaps, customers, pccFilterId]);

  const pccFilterMember = pccFilterId ? memberMap.get(pccFilterId) : undefined;

  const isOnLeave = useCallback((memberId: string, date: string) => leaveSet.has(`${memberId}|${date}`), [leaveSet]);

  // Approved time off that still has a live assignment sitting on top of it — visible
  // regardless of draft/published, so a manager can't miss it by not noticing the small
  // "on leave" tag buried in a day cell. This is the actual thing that needs fixing.
  const leaveConflicts = useMemo(() => {
    const customerMap = new Map(customers.map(c => [c.id, c.name]));
    return assignments
      .filter(a => isOnLeave(a.teamMemberId, a.assignmentDate))
      .map(a => {
        const m = memberMap.get(a.teamMemberId);
        return {
          id: a.id,
          memberName: m ? `${m.firstName} ${m.lastName}` : a.teamMemberId.slice(0, 8),
          date: a.assignmentDate,
          customerName: customerMap.get(a.customerId) ?? "Unknown account",
          customer: customers.find(c => c.id === a.customerId),
        };
      })
      .sort((x, y) => x.date.localeCompare(y.date));
  }, [assignments, isOnLeave, memberMap, customers]);

  const tabRow = (
    <div className="flex items-center gap-1 bg-white border rounded-lg p-1 w-fit">
      <button
        onClick={() => setView("schedule")}
        className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${view === "schedule" ? "bg-indigo-600 text-white" : "text-gray-600 hover:bg-gray-50"}`}
      >
        Schedule
      </button>
      <button
        onClick={() => setView("time-off")}
        className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${view === "time-off" ? "bg-indigo-600 text-white" : "text-gray-600 hover:bg-gray-50"}`}
      >
        Time Off
      </button>
    </div>
  );

  if (view === "time-off") {
    const reqTypeLabel: Record<TimeOffEntry["requestType"], string> = {
      time_off: "Time Off",
      sick: "Sick",
      personal: "Personal",
    };
    return (
      <div className="p-4 lg:p-6 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-gray-900">PCC Schedule</h1>
            <p className="text-sm text-gray-500">Time off — approved and rejected requests</p>
          </div>
          {tabRow}
        </div>

        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-600">Status</label>
          <select
            value={timeOffStatusFilter}
            onChange={e => setTimeOffStatusFilter(e.target.value as "" | "approved" | "rejected")}
            className="border rounded-lg px-2 py-1.5 text-sm"
          >
            <option value="">All (Approved + Rejected)</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>

        <div className="bg-white border rounded-xl overflow-hidden">
          {timeOffLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : timeOffList.length === 0 ? (
            <p className="text-sm text-gray-400 italic p-6 text-center">No time off requests found.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                  <th className="px-4 py-2 text-left font-semibold">PCC</th>
                  <th className="px-4 py-2 text-left font-semibold">Dates</th>
                  <th className="px-4 py-2 text-left font-semibold">Type</th>
                  <th className="px-4 py-2 text-left font-semibold">Reason</th>
                  <th className="px-4 py-2 text-left font-semibold">Status</th>
                  <th className="px-4 py-2 text-left font-semibold">Reviewed</th>
                  <th className="px-4 py-2 text-left font-semibold">Review Note</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {timeOffList.map(t => (
                  <tr key={t.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2.5 font-medium text-gray-700 whitespace-nowrap">{t.memberName}</td>
                    <td className="px-4 py-2.5 text-gray-600 whitespace-nowrap">
                      {t.startDate === t.endDate ? fmtDate(t.startDate) : `${fmtDate(t.startDate)} – ${fmtDate(t.endDate)}`}
                    </td>
                    <td className="px-4 py-2.5 text-gray-600">{reqTypeLabel[t.requestType]}</td>
                    <td className="px-4 py-2.5 text-gray-500 max-w-xs truncate" title={t.reason ?? ""}>{t.reason || "—"}</td>
                    <td className="px-4 py-2.5">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${t.status === "approved" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                        {t.status === "approved" ? "Approved" : "Rejected"}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-gray-500 whitespace-nowrap">{t.reviewedAt ? fmtDate(t.reviewedAt.slice(0, 10)) : "—"}</td>
                    <td className="px-4 py-2.5 text-gray-500 max-w-xs truncate" title={t.reviewNote ?? ""}>{t.reviewNote || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6 space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">PCC Schedule</h1>
          <p className="text-sm text-gray-500">{fmtWeekRange(weekStart)}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {tabRow}
          {/* Week navigation */}
          <div className="flex items-center gap-1 bg-white border rounded-lg">
            <button onClick={() => setWeekStart(w => addDays(w, -7))} className="p-2 hover:bg-gray-50 rounded-l-lg">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button onClick={() => setWeekStart(toMonday(today))} className="px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50 border-x">
              {weekStart === toMonday(today) ? "This week" : fmtWeekRange(weekStart)}
            </button>
            <button onClick={() => setWeekStart(w => addDays(w, 7))} className="p-2 hover:bg-gray-50 rounded-r-lg">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          {/* Status badge */}
          {period ? (
            <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
              isDraft ? "bg-amber-100 text-amber-700" : isPublished ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
            }`}>
              {isDraft ? "Draft" : isPublished ? "Published" : "Superseded"} v{period.version}
            </span>
          ) : (
            <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-400">No schedule</span>
          )}

          <div className="flex items-center gap-1.5">
            <div className="w-48">
              <TypeaheadSelect
                options={members.map(m => ({ id: m.id, label: `${m.firstName} ${m.lastName}` }))}
                value={pccFilterId}
                onChange={setPccFilterId}
                placeholder="Search PCC…"
              />
            </div>
            {pccFilterId && (
              <button onClick={() => setPccFilterId("")} title="Clear PCC filter"
                className="p-1.5 text-gray-400 hover:text-gray-600 border rounded-lg bg-white">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          <button onClick={() => setShowRecurring(true)}
            className="flex items-center gap-1.5 bg-white text-indigo-600 border border-indigo-200 px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-indigo-50"
            title="Manage recurring assignments (e.g. Saturday-only coverage)">
            <Repeat className="h-4 w-4" />
            Recurring Assignments
          </button>

          {/* Primary action: Generate Schedule */}
          {!period && (
            <button onClick={() => handleGenerate(false)} disabled={generating}
              className="flex items-center gap-1.5 bg-indigo-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
              {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              Generate Schedule
            </button>
          )}

          {/* Draft actions */}
          {isDraft && (
            <>
              <button onClick={() => handleGenerate(true)} disabled={generating || enhancing}
                className="flex items-center gap-1.5 bg-indigo-100 text-indigo-700 border border-indigo-200 px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-indigo-200 disabled:opacity-50"
                title="Clear unlocked assignments and regenerate">
                {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                Regenerate
              </button>
              <button onClick={handleEnhance} disabled={enhancing || generating}
                className="flex items-center gap-1.5 bg-purple-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-50"
                title="Optimize PCC assignments based on coverage history and workload balance">
                {enhancing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                {enhancing ? "Optimizing…" : "Optimize Schedule"}
              </button>
              <button onClick={handlePublish} disabled={publishing || enhancing}
                className="flex items-center gap-1.5 bg-green-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50">
                {publishing ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarCheck className="h-4 w-4" />}
                Publish
              </button>
            </>
          )}

          {/* Published action */}
          {isPublished && (
            <button onClick={handleRevise} disabled={revisioning}
              className="flex items-center gap-1.5 bg-amber-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-amber-700 disabled:opacity-50">
              {revisioning ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Revise
            </button>
          )}
        </div>
      </div>

      {/* Approved time off still sitting on a live assignment — always visible, draft or
          published, so this can't be missed the way a buried per-cell badge can be. */}
      {leaveConflicts.length > 0 && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertCircle className="h-4 w-4 text-red-500" />
            <span className="text-sm font-semibold text-red-700">
              {leaveConflicts.length} approved time-off conflict{leaveConflicts.length !== 1 ? "s" : ""} — still scheduled despite approved leave
            </span>
          </div>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {leaveConflicts.map(c => (
              <div
                key={c.id}
                onClick={() => isDraft && c.customer && setSidebar({ customer: c.customer, date: c.date })}
                className={`flex items-center gap-2 text-xs text-red-700 bg-white rounded px-3 py-1.5 border border-red-100 ${isDraft && c.customer ? "cursor-pointer hover:bg-red-50" : ""}`}
              >
                <span className="font-medium min-w-0 truncate flex-1">{c.memberName}</span>
                <span className="text-red-500 whitespace-nowrap">{fmtDate(c.date)} · {c.customerName}</span>
                {isDraft && <span className="text-red-400 whitespace-nowrap underline">Fix now</span>}
              </div>
            ))}
          </div>
          {!isDraft && (
            <p className="mt-2 text-xs text-red-500">Click "Revise" above to fix coverage for these shifts.</p>
          )}
        </div>
      )}

      {/* Gap report */}
      {showGaps && <GapReport gaps={gaps} onDismiss={() => setShowGaps(false)} />}

      {/* PCC filter banner */}
      {pccFilterMember && (
        <div className="flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2 text-sm text-indigo-700">
          <span>Showing shifts for <span className="font-semibold">{pccFilterMember.firstName} {pccFilterMember.lastName}</span> this week — {displayCustomers.length} account{displayCustomers.length === 1 ? "" : "s"}.</span>
          {isDraft && (
            <button onClick={() => setShowBulkSwap(true)}
              className="ml-auto flex items-center gap-1.5 bg-white text-indigo-600 border border-indigo-300 px-2.5 py-1 rounded-lg text-xs font-medium hover:bg-indigo-100"
              title="Replace this PCC across multiple accounts for one day, in one action">
              <ArrowRightLeft className="h-3.5 w-3.5" />
              Bulk Swap Coverage
            </button>
          )}
          <button onClick={() => setPccFilterId("")} className={isDraft ? "text-indigo-500 hover:text-indigo-700 underline" : "ml-auto text-indigo-500 hover:text-indigo-700 underline"}>Clear</button>
        </div>
      )}

{loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border bg-white shadow-sm">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 w-48">
                  Account
                </th>
                {days.map((d, i) => (
                  <th key={d} className="px-3 py-3 text-center text-xs font-semibold text-gray-600 min-w-[110px]">
                    <div>{DAY_LABELS[i]}</div>
                    <div className="text-gray-400 font-normal">{fmtDate(d)}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">

              {displayCustomers.map(customer => {
                const tz = customer.reportTimezone || "America/Los_Angeles";
                return (
                  <tr key={customer.id} className="hover:bg-gray-50/50">
                    <td className="px-4 py-2.5">
                      <p className="font-medium text-gray-800 text-xs leading-tight">{customer.name}</p>
                      <p className="text-gray-400 text-xs">{tz.replace("America/", "")}</p>
                    </td>
                    {days.map(date => {
                      const allCellAssignments = assignmentMap.get(`${customer.id}|${date}`) ?? [];
                      // Clicking the cell still opens every assignment for that day (unfiltered) so
                      // managers can keep editing everyone — only the display below is narrowed.
                      // Also keep the assignment visible if this PCC was just swapped out of it
                      // (replacesTeamMemberId points back to them), so a swap shows the replacement
                      // and "Covering" badge instead of the row silently reading "other PCC".
                      const cellAssignments = pccFilterId
                        ? allCellAssignments.filter(a => a.teamMemberId === pccFilterId || a.replacesTeamMemberId === pccFilterId)
                        : allCellAssignments;
                      return (
                        <td key={date}
                          onClick={() => isDraft && setSidebar({ customer, date })}
                          className={`px-2 py-2 text-center align-top ${isDraft ? "cursor-pointer hover:bg-blue-50" : ""}`}>
                          {cellAssignments.length === 0 ? (
                            pccFilterId && allCellAssignments.length > 0 ? (
                              <span className="text-[10px] text-gray-300 italic">other PCC</span>
                            ) : (
                              isDraft && <span className="inline-block text-gray-300 hover:text-blue-400 text-lg leading-none">+</span>
                            )
                          ) : (
                            <div className="space-y-1">
                              {cellAssignments.map(a => {
                                const m = memberMap.get(a.teamMemberId);
                                const onLeave = isOnLeave(a.teamMemberId, date);
                                const replacesMember = a.replacesTeamMemberId ? memberMap.get(a.replacesTeamMemberId) : null;
                                const isReplacement = !!a.replacesTeamMemberId;
                                const colorClass = onLeave
                                  ? "bg-amber-100 text-amber-800 ring-1 ring-amber-300"
                                  : isReplacement
                                  ? "bg-orange-100 text-orange-800 ring-1 ring-orange-300"
                                  : a.aiEnhanced
                                  ? "bg-purple-100 text-purple-800"
                                  : "bg-blue-100 text-blue-800";
                                return (
                                  <div key={a.id} className={`text-xs rounded px-1.5 py-0.5 leading-snug ${colorClass}`}>
                                    <div className="font-medium truncate max-w-[90px]">
                                      {m ? `${m.firstName} ${m.lastName.charAt(0)}.` : "?"}
                                      {a.isLocked && " 🔒"}
                                      {onLeave && " 🏖"}
                                      {isReplacement && " 🔄"}
                                      {a.aiEnhanced && " ✨"}
                                    </div>
                                    <div className="text-[10px] font-semibold opacity-80">{fmtTimeRange(a.startsAt, a.endsAt, tz)}</div>
                                    {onLeave && <div className="text-[10px] font-semibold text-amber-700">On leave</div>}
                                    {isReplacement && (
                                      <div className="text-[10px] font-semibold text-orange-700 truncate">
                                        Covering {replacesMember ? `${replacesMember.firstName} ${replacesMember.lastName.charAt(0)}.` : "—"}
                                      </div>
                                    )}
                                    {a.aiEnhanced && a.aiReason && <div className="text-[10px] text-purple-600 break-words leading-tight mt-0.5">✨ {a.aiReason}</div>}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}


            </tbody>
          </table>

          {displayCustomers.length === 0 && (
            <div className="text-center py-10 text-gray-400 text-sm">No active customers found</div>
          )}
        </div>
      )}

      {/* Legend */}
      {period && (
        <div className="flex items-center gap-4 text-xs text-gray-500 flex-wrap">
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-blue-100 inline-block" /> Assigned</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-purple-100 inline-block" /> ✨ AI enhanced</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-amber-100 inline-block" /> 🏖 Approved leave</span>
          <span>🔒 Locked (survives regeneration)</span>
          {isDraft && <span className="text-blue-500">Click any cell to override an assignment</span>}
        </div>
      )}

      {/* Sidebar */}
      {sidebar && period && (
        <AssignmentSidebar
          periodId={period.id}
          customer={sidebar.customer}
          date={sidebar.date}
          assignments={assignments}
          allMembers={members}
          onClose={() => setSidebar(null)}
          onRefresh={loadPeriod}
        />
      )}

      {/* Recurring Assignments modal */}
      {showRecurring && (
        <RecurringTemplatesModal
          customers={customers}
          members={members}
          onClose={() => setShowRecurring(false)}
        />
      )}

      {/* Bulk Swap Coverage modal */}
      {showBulkSwap && pccFilterMember && (
        <BulkSwapModal
          pccMember={pccFilterMember}
          weekAssignments={assignments.filter(a => a.teamMemberId === pccFilterMember.id)}
          allMembers={members}
          customers={customers}
          onClose={() => setShowBulkSwap(false)}
          onRefresh={loadPeriod}
        />
      )}
    </div>
  );
}
